import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { AccountVariableDefinition } from 'dashboard/models/account-variable-definition';
import { Case } from 'dashboard/models/case';
import { CaseTemplate } from 'dashboard/models/case-template';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { Inquiry } from 'dashboard/models/inquiry';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Selfie } from 'dashboard/models/selfie';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { PaginationParams } from 'lib/interfaces/api';
import { Relations } from 'lib/relations';

export interface AccountState {
  account: Account | undefined;
  inquiries: Inquiry[] | undefined;
  documentPaginationParams?: PaginationParams;
  documentPageSize?: number;
  documentCurrentPage?: number;
  inquiryPaginationParams?: PaginationParams;
  inquiryPageSize?: number;
  inquiryCurrentPage?: number;
  verificationPaginationParams?: PaginationParams;
  verificationPageSize?: number;
  verificationCurrentPage?: number;
  reportPaginationParams?: PaginationParams;
  reportPageSize?: number;
  reportCurrentPage?: number;
  accountStatus: AccountStatus | undefined;
  accountType: AccountType | undefined;
  tags: Tag[] | undefined;
  redactor: User | undefined;
  reports: Report[] | undefined;
  devices: Device[] | undefined;
  documents: Document[] | undefined;
  cases: Case[] | undefined;
  caseTemplates: CaseTemplate[];
  relations: Relations | undefined;
  reportTemplates: ReportTemplate[];
  selfies: Selfie[] | undefined;
  verifications: Verification[] | undefined;
  variableDefinitions: AccountVariableDefinition[];
  listMatches: ListMatch[];
  lists: List[];
  listItems: ListItem[];
}

const initialState: AccountState = {
  account: undefined,
  inquiries: undefined,
  accountStatus: undefined,
  accountType: undefined,
  tags: undefined,
  redactor: undefined,
  reports: undefined,
  devices: undefined,
  documents: undefined,
  cases: undefined,
  caseTemplates: [],
  documentPaginationParams: undefined,
  inquiryPaginationParams: undefined,
  verificationPaginationParams: undefined,
  reportPaginationParams: undefined,
  documentPageSize: undefined,
  documentCurrentPage: undefined,
  inquiryPageSize: undefined,
  inquiryCurrentPage: undefined,
  verificationPageSize: undefined,
  verificationCurrentPage: undefined,
  reportPageSize: undefined,
  reportCurrentPage: undefined,
  relations: undefined,
  reportTemplates: [],
  selfies: [],
  verifications: [],
  variableDefinitions: [],
  listMatches: [],
  lists: [],
  listItems: [],
};

interface SaveAccountDocumentsPayload {
  documents: Document[];
  paginationParams?: PaginationParams;
}

interface SaveAccountInquiriesPayload {
  inquiries: Inquiry[];
  paginationParams?: PaginationParams;
}

interface SaveAccountReportsPayload {
  reports: Report[];
  paginationParams?: PaginationParams;
}

interface SaveAccountVerificationsPayload {
  verifications: Verification[];
  paginationParams?: PaginationParams;
}

const { reducer, actions } = createSlice({
  name: 'account',
  initialState,
  reducers: {
    clearAccount: () => {
      return initialState;
    },
    setAccount: (state, action: PayloadAction<Account>) => {
      state.account = castDraft(action.payload);
    },
    saveAccountDocuments: (state, action: PayloadAction<SaveAccountDocumentsPayload>) => {
      state.documents = castDraft(action.payload.documents);
      state.documentPaginationParams = action.payload.paginationParams;
    },
    saveAccountInquiries: (state, action: PayloadAction<SaveAccountInquiriesPayload>) => {
      state.inquiries = castDraft(action.payload.inquiries);
      state.inquiryPaginationParams = action.payload.paginationParams;
    },
    saveAccountReports: (state, action: PayloadAction<SaveAccountReportsPayload>) => {
      state.reports = action.payload.reports;
      state.reportPaginationParams = action.payload.paginationParams;
    },
    saveAccountVerifications: (state, action: PayloadAction<SaveAccountVerificationsPayload>) => {
      state.verifications = action.payload.verifications;
      state.verificationPaginationParams = action.payload.paginationParams;
    },
    setAccountStatus: (state, action: PayloadAction<AccountStatus | undefined>) => {
      state.accountStatus = action.payload;
    },
    setAccountType: (state, action: PayloadAction<AccountType | undefined>) => {
      state.accountType = action.payload;
    },
    setTagsSummary: (state, action: PayloadAction<Tag[]>) => {
      state.tags = action.payload;
    },
    setAccountRedactor: (state, action: PayloadAction<User | undefined>) => {
      state.redactor = action.payload;
    },
    setAccountReports: (state, action: PayloadAction<Report[]>) => {
      state.reports = action.payload;
    },
    setAccountDevices: (state, action: PayloadAction<Device[]>) => {
      state.devices = action.payload;
    },
    setAccountDocuments: (state, action: PayloadAction<Document[]>) => {
      state.documents = castDraft(action.payload);
    },
    setAccountCases: (state, action: PayloadAction<Case[]>) => {
      state.cases = castDraft(action.payload);
    },
    setAccountCaseTemplates: (state, action: PayloadAction<CaseTemplate[]>) => {
      state.caseTemplates = action.payload;
    },
    setAccountSelfies: (state, action: PayloadAction<Selfie[]>) => {
      state.selfies = action.payload;
    },
    setDocumentCurrentPage: (state, action: PayloadAction<number>) => {
      state.documentCurrentPage = action.payload;
    },
    setDocumentPageSize: (state, action: PayloadAction<number>) => {
      state.documentPageSize = action.payload;
    },
    setInquiryCurrentPage: (state, action: PayloadAction<number>) => {
      state.inquiryCurrentPage = action.payload;
    },
    setInquiryPageSize: (state, action: PayloadAction<number>) => {
      state.inquiryPageSize = action.payload;
    },
    setVerificationCurrentPage: (state, action: PayloadAction<number>) => {
      state.verificationCurrentPage = action.payload;
    },
    setVerificationPageSize: (state, action: PayloadAction<number>) => {
      state.verificationPageSize = action.payload;
    },
    setReportCurrentPage: (state, action: PayloadAction<number>) => {
      state.reportCurrentPage = action.payload;
    },
    setReportPageSize: (state, action: PayloadAction<number>) => {
      state.reportPageSize = action.payload;
    },
    setAccountRelations: (state, action: PayloadAction<Relations>) => {
      state.relations = action.payload;
    },
    setAccountReportTemplates: (state, action: PayloadAction<ReportTemplate[]>) => {
      state.reportTemplates = action.payload;
    },
    setAccountVariableDefinitions: (state, action: PayloadAction<AccountVariableDefinition[]>) => {
      state.variableDefinitions = action.payload;
    },
    setAccountListMatches: (state, action: PayloadAction<ListMatch[]>) => {
      state.listMatches = action.payload;
    },
    setAccountLists: (state, action: PayloadAction<List[]>) => {
      state.lists = action.payload;
    },
    setAccountListItems: (state, action: PayloadAction<ListItem[]>) => {
      state.listItems = action.payload;
    },
  },
});

export const {
  clearAccount,
  setAccount,
  saveAccountDocuments,
  saveAccountInquiries,
  saveAccountReports,
  saveAccountVerifications,
  setAccountStatus,
  setAccountType,
  setTagsSummary,
  setAccountRedactor,
  setAccountReports,
  setAccountDevices,
  setAccountDocuments,
  setAccountCases,
  setAccountCaseTemplates,
  setAccountSelfies,
  setDocumentCurrentPage,
  setDocumentPageSize,
  setInquiryCurrentPage,
  setInquiryPageSize,
  setReportCurrentPage,
  setReportPageSize,
  setVerificationCurrentPage,
  setVerificationPageSize,
  setAccountRelations,
  setAccountReportTemplates,
  setAccountVariableDefinitions,
  setAccountListMatches,
  setAccountLists,
  setAccountListItems,
} = actions;

export const accountReducer = reducer;
